import { IS_DEV } from '../../viteEnv';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AcessTokeScope,
  DateField,
  EmailField,
  EnrollmentFormDenormalized,
  EnrollmentFormFieldDenormalized,
  EnrollmentFormStatus,
  FieldDataDestinationType,
  FIELDS_WHICH_CAN_RESTRICT_TO_EXISTING_VALUES,
  FieldType,
  getUserFieldTarget,
  IEnrollmentForm,
  IEnrollmentFormSubmission,
  TextField,
  TextFieldDenormalized,
  TextRestriction
} from '@dadohr/shared/domainTypes/enrollmentForms';
import {
  ExperienceAnchorType,
  IExperienceContact,
  IExperienceContactMail,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { DefaultDadoField } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { compact, memoize } from 'lodash';
import { useCollection } from 'react-firebase-hooks/firestore';
import shortid from 'shortid';
import ENV from '../../env.json';
import { store, useMappedLoadingValue } from '../firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import { toDenormalizedOrg } from '../orgs';
import { now } from '../time';
import { getUsersByOrgId, getUserValue, useUserFieldLableMap } from '../user';
import {
  toEnrollmentFormDoc,
  toEnrollmentFormSubmissionDoc
} from './documents';
import { generateAccessToken } from './instance';

const COLLECTION = FS.enrollmentForms;

const getEnrollmentFormByTemplateQuery = (orgId: string, tplId: string) => {
  const templateIdKey: keyof IEnrollmentForm = 'templateId';
  return store()
    .collection(COLLECTION)
    .where('orgId', '==', orgId)
    .where(templateIdKey, '==', tplId);
};

export const getEnrollmentFormByTemplate = (orgId: string, tplId: string) => {
  return getEnrollmentFormByTemplateQuery(orgId, tplId)
    .get()
    .then((s) => {
      const d = s.docs[0];
      return d ? toEnrollmentFormDoc(d) : null;
    });
};

export const useEnrollmentFormByTemplateOrGetEmptyDefault = (
  tpl: Doc<IExperienceTemplate>,
  userId: string
) => {
  const userFieldLabelMap = useUserFieldLableMap();
  const query = getEnrollmentFormByTemplateQuery(tpl.data.orgId, tpl.id);
  return useMappedLoadingValue(useCollection(query), (queryResult) => {
    const d = queryResult.docs[0];
    return d
      ? toEnrollmentFormDoc(d)
      : getEmptyEnrollmentFormDoc(tpl.data, userId, userFieldLabelMap);
  });
};

const getEmptyEnrollmentFormDoc = (
  template: IExperienceTemplate,
  userId: string,
  userFieldLabelMap: { [key: string]: string }
): Doc<IEnrollmentForm> => {
  const id = shortid();

  const newEnrollmentForm: Doc<IEnrollmentForm> = {
    id,
    collection: COLLECTION,

    data: {
      id,
      orgId: template.orgId,
      templateId: template.id,
      accessTokens: [],
      accessTokensInfo: {},
      status: EnrollmentFormStatus.DRAFT,
      lastModifiedAt: now(),
      modifiedBy: userId,
      introduction: {
        t: '',
        v: 0
      },
      fields: [],
      sso: { required: false },
      autoEnroll: {
        active: false
      }
    }
  };

  const firstNameField: TextField = {
    id: DefaultDadoField.FIRST_NAME,
    type: FieldType.TEXT,
    mandatory: true,
    targets: [
      {
        type: FieldDataDestinationType.USER_PROFILE,
        id: shortid(),
        userField: DefaultDadoField.FIRST_NAME
      }
    ],
    renderParameters: {
      displayName: userFieldLabelMap[DefaultDadoField.FIRST_NAME]
    },
    validationParameters: {
      restrictAnswerChoice: false,
      allowedValuesList: null,
      restrictionType: null
    }
  };

  const lastNameField: TextField = {
    id: DefaultDadoField.LAST_NAME,
    type: FieldType.TEXT,
    mandatory: true,
    targets: [
      {
        type: FieldDataDestinationType.USER_PROFILE,
        id: shortid(),
        userField: DefaultDadoField.LAST_NAME
      }
    ],
    renderParameters: {
      displayName: userFieldLabelMap[DefaultDadoField.LAST_NAME]
    },
    validationParameters: {
      restrictAnswerChoice: false,
      allowedValuesList: null,
      restrictionType: null
    }
  };

  const personalEmail: EmailField = {
    id: DefaultDadoField.PERSONAL_EMAIL,
    type: FieldType.EMAIL,
    mandatory: true,
    targets: [
      {
        type: FieldDataDestinationType.USER_PROFILE,
        id: shortid(),
        userField: DefaultDadoField.PERSONAL_EMAIL
      }
    ],
    renderParameters: {
      displayName: userFieldLabelMap[DefaultDadoField.PERSONAL_EMAIL]
    },
    validationParameters: {}
  };

  newEnrollmentForm.data.fields.push(
    personalEmail,
    firstNameField,
    lastNameField
  );

  if (template.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    const achordField: DateField = {
      id: template.anchor.field,
      type: FieldType.DATE,
      mandatory: true,
      targets: [
        {
          type: FieldDataDestinationType.USER_PROFILE,
          id: shortid(),
          userField: template.anchor.field
        }
      ],
      renderParameters: {
        displayName: userFieldLabelMap[template.anchor.field]
      },
      validationParameters: {
        restrictToInterval: false,
        from: null,
        to: null
      }
    };
    newEnrollmentForm.data.fields.push(achordField);
  }

  return newEnrollmentForm;
};

export const toStandaloneAppEnrollmentFormUrl = (
  enrollmentForm: IEnrollmentForm
) => {
  const hostname = IS_DEV
    ? ENV.domains.standaloneApp.local
    : ENV.domains.standaloneApp.public;

  const accessToken = enrollmentForm.accessTokens[0];
  return `${hostname}/enrollmentForms/${accessToken}`;
};

const formIdKey: keyof IEnrollmentFormSubmission = 'formId';
const experienceEnrollFormsCollection = () =>
  store().collection(FS.enrollmentFormsSubmissions);
const DELIMITER = '@@@';
const enrollmentSubmissionByEnrollmentFormId = createCollectionListenerStore(
  (key) => {
    const [orgId, enrollmentFormId] = key.split(DELIMITER);
    return new CollectionListener(
      experienceEnrollFormsCollection()
        .where('orgId', '==', orgId)
        .where(formIdKey, '==', enrollmentFormId),
      toEnrollmentFormSubmissionDoc
    );
  }
);

export const useEnrollSubmissionById = (formId: string, orgId: string) => {
  return useCollectionListener(
    enrollmentSubmissionByEnrollmentFormId([orgId, formId].join(DELIMITER))
  );
};

const isMailContact = (c: IExperienceContact): c is IExperienceContactMail => {
  return c.type === 'MAIL';
};

const _getExistingUserValuesForFiels = async (
  userField: string,
  orgId: string
) => {
  const users = await getUsersByOrgId(orgId);
  const n = now();
  const values = Array.from(
    new Set(
      compact(users.map((u) => getUserValue(u.data, userField, n) as string))
    )
  );
  return values;
};

const getExistingUserValuesForFiels = memoize(
  _getExistingUserValuesForFiels,
  (userField: string, orgId: string) => `${orgId}_${userField}`
);

const getTextFieldDenormalized = async (
  field: TextField,
  org: IOrganization
): Promise<TextFieldDenormalized> => {
  let existingValues = null;
  const userFieldTarget = getUserFieldTarget(field);
  if (
    field.validationParameters.restrictAnswerChoice &&
    field.validationParameters.restrictionType === TextRestriction.EXISTING &&
    userFieldTarget &&
    FIELDS_WHICH_CAN_RESTRICT_TO_EXISTING_VALUES.includes(
      userFieldTarget?.userField as any
    )
  ) {
    existingValues = await getExistingUserValuesForFiels(
      userFieldTarget.userField,
      org.id
    );
  }

  const fieldRenderInfo: EnrollmentFormFieldDenormalized = {
    id: field.id,
    type: field.type,
    renderParameters: { ...field.renderParameters, existingValues },
    validationParameters: field.validationParameters
  };
  return fieldRenderInfo;
};

export const toEnrollmentFormDenormalized = async (
  enrolmentForm: IEnrollmentForm,
  tpl: IExperienceTemplate,
  org: IOrganization
): Promise<EnrollmentFormDenormalized> => {
  // Pre-fetch user data, which might be used in getTextFielDenormalized
  // This is a dirty fix for https://app.shortcut.com/emplexp/story/9046/the-enrollment-form-modal-for-mixpanel-s-mentorship-template-is-missing-a-preview
  // It turns out that if several fields start asking for this at the same time, that
  // one call can get stuck due to the nature of the underlying collection listener
  // If we pre-fetch we guarantee that the inner calls all get cached results back - which
  // circumvents this problem.
  await getUsersByOrgId(org.id);
  const mailContact = tpl.contacts.filter(isMailContact)[0] || null;
  const fields = await Promise.all(
    enrolmentForm.fields.map(async (field) => {
      switch (field.type) {
        case FieldType.TEXT:
          return await getTextFieldDenormalized(field, org);
        default:
          // The typechecker can't see that the type matches the render and validatio parameters unless
          // you explecitely go throug each case in the switch statement, any field that needs any special
          // treatment should be treated accordingly in the switch statemen
          const fieldRenderInfo = {
            id: field.id,
            type: field.type,
            renderParameters: { ...field.renderParameters },
            validationParameters: field.validationParameters
          } as EnrollmentFormFieldDenormalized;
          return fieldRenderInfo;
      }
    })
  );

  return {
    accessToken: '',
    mailContact: mailContact,
    experienceTitle: tpl.title,
    introduction: enrolmentForm.introduction,
    org: toDenormalizedOrg(org, tpl.overrides?.org || null),
    fields: fields,
    sso: enrolmentForm.sso
  };
};

export const getDuplicateEnrollmentForm = (
  enrollmentForm: IEnrollmentForm,
  modifiedBy: string,
  templateId: string
) => {
  const newId = shortid();
  const n = now();
  const newFields = enrollmentForm.fields.map((a) => ({
    ...a,
    targets: a.targets.map((t) => ({ ...t, id: shortid() }))
  }));
  const newAccessToken = generateAccessToken();
  const scopes: AcessTokeScope[] = [];
  const status =
    enrollmentForm.status === EnrollmentFormStatus.DRAFT
      ? EnrollmentFormStatus.DRAFT
      : EnrollmentFormStatus.INACTIVE;

  const duplicate: Doc<IEnrollmentForm> = {
    id: newId,
    collection: FS.enrollmentForms,
    data: {
      ...enrollmentForm,
      id: newId,
      accessTokens: [newAccessToken],
      accessTokensInfo: {
        [newAccessToken]: {
          acessToken: newAccessToken,
          expirationDate: null,
          scopes
        }
      },
      lastModifiedAt: n,
      templateId: templateId,
      modifiedBy: modifiedBy,
      fields: newFields,
      status: status
    }
  };
  return duplicate;
};
